import React, { memo } from 'react';
import { Camera, CheckCircle, Star, TrendingUp } from 'lucide-react';
import { ITrip } from '../../../../Models/trips/trip.model';
import { getBookedActivityIds } from '../../../../utils/itinerary-activity-utils';
import './overview-panels.scss';

interface TripRecapOverviewProps {
  trip: ITrip;
}

const TripRecapOverview: React.FC<TripRecapOverviewProps> = ({ trip }) => {
  const totalActivities = trip.totalActivities || 0;
  const bookedActivities = getBookedActivityIds(trip.itineraries).size;

  const completionPercentage =
    totalActivities > 0
      ? Math.round((bookedActivities / totalActivities) * 100)
      : 0;

  const photoCount = trip.photoCount || 0;
  const photosWithLocation = trip.photosWithLocation || 0;
  const photosWithCaption = trip.photosWithCaption || 0;

  // Calculate activities with user ratings
  const activitiesWithRating =
    trip.activities?.filter(
      (activity) => activity.userRating && activity.userRating > 0
    ).length || 0;

  const averageRating =
    trip.activities?.reduce((sum, activity) => {
      return sum + (activity.userRating || 0);
    }, 0) / Math.max(activitiesWithRating, 1);

  return (
    <div className='overview-panel-content'>
      <h4 className='panel-title font-libre'>Recap</h4>

      <div className='panel-stats'>
        <div className='stat-item highlight'>
          <CheckCircle
            size={20}
            className='stat-icon'
          />
          <div className='stat-info'>
            <div className='stat-label'>Trip Completion</div>
            <div className='stat-value large'>{completionPercentage}%</div>
            <div className='stat-sub'>
              {bookedActivities} of {totalActivities} activities booked
            </div>
          </div>
        </div>

        <div className='progress-bar-container'>
          <div
            className='progress-bar-fill'
            style={{ width: `${completionPercentage}%` }}
          />
        </div>

        <div className='stat-grid'>
          <div className='stat-item-small'>
            <Camera
              size={16}
              className='stat-icon-small'
            />
            <div className='stat-info'>
              <div className='stat-label'>Photos</div>
              <div className='stat-value'>{photoCount}</div>
            </div>
          </div>
          <div className='stat-item-small'>
            <Star
              size={16}
              className='stat-icon-small'
            />
            <div className='stat-info'>
              <div className='stat-label'>Avg Rating</div>
              <div className='stat-value'>
                {activitiesWithRating > 0 ? averageRating.toFixed(1) : '—'}
              </div>
            </div>
          </div>
        </div>

        {photoCount > 0 && (
          <div className='stat-section'>
            <div className='section-title'>
              <TrendingUp size={16} />
              <span>Photo Stats</span>
            </div>
            <div className='photo-stats'>
              <div className='photo-stat-item'>
                <span className='photo-stat-label'>With Location</span>
                <span className='photo-stat-value'>
                  {photosWithLocation} (
                  {Math.round((photosWithLocation / photoCount) * 100)}%)
                </span>
              </div>
              <div className='photo-stat-item'>
                <span className='photo-stat-label'>With Caption</span>
                <span className='photo-stat-value'>
                  {photosWithCaption} (
                  {Math.round((photosWithCaption / photoCount) * 100)}%)
                </span>
              </div>
            </div>
          </div>
        )}

        {totalActivities === 0 && (
          <div className='empty-state'>
            <p>No activities to recap</p>
            <p className='empty-sub'>Start planning your trip</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(TripRecapOverview);
